/**
 * Adaptive Learning Service
 * 
 * Uses Vertex AI / Gemini to:
 * - Analyze student performance patterns
 * - Generate personalized hints when stuck
 * - Recommend next challenges based on mastery
 * - Create parent-friendly progress summaries
 * 
 * Designed for minimal API calls with aggressive caching
 */

import { tracker } from './tracking';

// Types
export interface StudentProfile {
  userId: string;
  isChild: boolean;
  currentLevel: number;
  totalXP: number;
  streakDays: number;
  masteredSkills: string[];
  strugglingSkills: string[];
  recentAttempts: AttemptSummary[];
  learningStyle?: 'visual' | 'verbal' | 'kinesthetic';
  preferredDifficulty?: 'easy' | 'medium' | 'hard';
}

export interface AttemptSummary {
  templateId: string;
  skillTag: string;
  score: number;
  passed: boolean;
  durationSec: number;
  timestamp: string;
  errorPatterns?: string[];
}

export interface ChallengeRecommendation {
  templateId: string;
  title: string;
  skillTag: string;
  reason: string;
  difficulty: 'easy' | 'medium' | 'hard';
  estimatedTime: string;
  xpReward: number;
  confidence: number;
}

export interface HintResponse {
  hint: string;
  category: 'conceptual' | 'procedural' | 'example';
  difficulty: 'gentle' | 'moderate' | 'direct';
  followUp?: string;
}

export interface ProgressSummary {
  headline: string;
  strengths: string[];
  growthAreas: string[];
  recommendedActions: string[];
  celebrationMoment?: string;
}

// Cache for reducing API calls
const responseCache = new Map<string, { data: any; expiry: number }>();
const CACHE_TTL = {
  recommendations: 30 * 60 * 1000,  // 30 min
  hints: 10 * 60 * 1000,            // 10 min
  summaries: 60 * 60 * 1000,        // 1 hour
};

function getCacheKey(operation: string, params: Record<string, unknown>): string {
  return `${operation}:${JSON.stringify(params)}`;
}

function getFromCache<T>(key: string): T | null {
  const cached = responseCache.get(key);
  if (cached && cached.expiry > Date.now()) {
    return cached.data as T;
  }
  responseCache.delete(key);
  return null;
}

function setCache(key: string, data: any, ttl: number) {
  responseCache.set(key, { data, expiry: Date.now() + ttl });
}

// Vertex AI configuration
const VERTEX_CONFIG = {
  projectId: import.meta.env.VITE_VERTEX_PROJECT_ID,
  location: import.meta.env.VITE_VERTEX_LOCATION || 'us-central1',
  model: 'gemini-1.5-flash', // Fast and cost-effective
};

async function callVertexAI(params: {
  prompt: string;
  systemInstruction?: string;
  maxTokens?: number;
  temperature?: number;
  userId?: string;
  isChild?: boolean;
  operation: string;
}): Promise<string | null> {
  const { prompt, systemInstruction, maxTokens = 500, temperature = 0.7, userId, isChild, operation } = params;

  // Check cost limits before calling
  const costCheck = tracker.checkCostLimits('ai', 0.002, userId); // Estimate ~2000 tokens
  if (!costCheck.allowed) {
    console.warn('[AdaptiveLearning] Cost limit reached:', costCheck.reason);
    return null;
  }

  // Track the request
  tracker.trackAI({
    provider: 'gemini',
    model: VERTEX_CONFIG.model,
    operation,
    userId,
    isChild,
    success: false, // Will update on success
  });

  try {
    const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
    
    if (!apiKey) {
      console.warn('[AdaptiveLearning] No Gemini API key configured');
      return null;
    }

    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/${VERTEX_CONFIG.model}:generateContent?key=${apiKey}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: prompt }] }],
          systemInstruction: systemInstruction 
            ? { parts: [{ text: systemInstruction }] }
            : undefined,
          generationConfig: {
            maxOutputTokens: maxTokens,
            temperature,
          },
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`Gemini API error: ${response.status}`);
    }

    const data = await response.json();
    const text = data.candidates?.[0]?.content?.parts?.[0]?.text || null;

    // Track successful completion with estimated tokens
    const inputTokens = Math.ceil(prompt.length / 4);
    const outputTokens = text ? Math.ceil(text.length / 4) : 0;

    tracker.trackAI({
      provider: 'gemini',
      model: VERTEX_CONFIG.model,
      operation,
      inputTokens,
      outputTokens,
      userId,
      isChild,
      success: true,
    });

    return text;
  } catch (err) {
    console.error('[AdaptiveLearning] API error:', err);
    return null;
  }
}

// Gaming-native system instructions
const SYSTEM_INSTRUCTIONS = {
  recommendations: `You are a game designer helping create personalized learning quests. 
Use gaming terminology: quests, XP, leveling up, power-ups, boss battles, side quests.
Keep recommendations fun and motivating. Never be discouraging.
Always respond with valid JSON array.`,

  hints: `You are a friendly game guide helping a player who's stuck.
Use encouraging, gamer-friendly language. Reference "respawning" and "trying again."
Keep hints short and actionable. Start with gentle nudges, only get more direct if asked.
Never give away the answer directly - guide them to discover it.`,

  parentSummary: `You are creating a progress report for a parent about their child's learning journey.
Be positive but honest. Highlight victories first, then growth opportunities.
Use specific examples. Suggest concrete ways parents can support.
Keep it brief - parents are busy.`,
};

/**
 * Get personalized challenge recommendations
 */
export async function getRecommendations(
  profile: StudentProfile,
  availableChallenges: Array<{ templateId: string; title: string; skillTag: string; difficulty: string }>
): Promise<ChallengeRecommendation[]> {
  const cacheKey = getCacheKey('recommendations', { 
    userId: profile.userId, 
    level: profile.currentLevel,
    recentCount: profile.recentAttempts.length,
  });

  const cached = getFromCache<ChallengeRecommendation[]>(cacheKey);
  if (cached) {
    tracker.trackAI({
      provider: 'gemini',
      model: VERTEX_CONFIG.model,
      operation: 'recommendation',
      cached: true,
      userId: profile.userId,
      isChild: profile.isChild,
      success: true,
    });
    return cached;
  }

  const prompt = `Based on this player profile, recommend the top 3 quests they should tackle next:

PLAYER STATS:
- Level: ${profile.currentLevel}
- Total XP: ${profile.totalXP}
- Current streak: ${profile.streakDays} days
- Mastered skills: ${profile.masteredSkills.join(', ') || 'None yet'}
- Struggling with: ${profile.strugglingSkills.join(', ') || 'Nothing identified'}

RECENT QUEST HISTORY:
${profile.recentAttempts.slice(0, 5).map(a => 
  `- ${a.skillTag}: ${a.passed ? 'CLEARED' : 'FAILED'} (${Math.round(a.score * 100)}%)`
).join('\n')}

AVAILABLE QUESTS:
${availableChallenges.map(c => `- ${c.templateId}: "${c.title}" [${c.skillTag}] (${c.difficulty})`).join('\n')}

Return JSON array with format:
[{
  "templateId": "...",
  "title": "...",
  "skillTag": "...",
  "reason": "Why this quest is perfect for them (use gaming language)",
  "difficulty": "easy|medium|hard",
  "estimatedTime": "5-10 min",
  "xpReward": 25,
  "confidence": 0.85
}]`;

  const response = await callVertexAI({
    prompt,
    systemInstruction: SYSTEM_INSTRUCTIONS.recommendations,
    maxTokens: 800,
    temperature: 0.7,
    userId: profile.userId,
    isChild: profile.isChild,
    operation: 'recommendation',
  });

  if (!response) {
    return getDefaultRecommendations(profile, availableChallenges);
  }

  try {
    // Extract JSON from response
    const jsonMatch = response.match(/\[[\s\S]*\]/);
    if (!jsonMatch) throw new Error('No JSON found');
    
    const recommendations = JSON.parse(jsonMatch[0]) as ChallengeRecommendation[];
    setCache(cacheKey, recommendations, CACHE_TTL.recommendations);
    return recommendations;
  } catch (err) {
    console.error('[AdaptiveLearning] Failed to parse recommendations:', err);
    return getDefaultRecommendations(profile, availableChallenges);
  }
}

/**
 * Generate a hint for a stuck student
 */
export async function getHint(params: {
  question: string;
  studentAnswer?: string;
  skillTag: string;
  attemptNumber: number;
  profile: StudentProfile;
}): Promise<HintResponse> {
  const { question, studentAnswer, skillTag, attemptNumber, profile } = params;

  const cacheKey = getCacheKey('hint', { 
    question: question.slice(0, 50),
    skillTag,
    attemptNumber,
  });

  const cached = getFromCache<HintResponse>(cacheKey);
  if (cached) {
    return cached;
  }

  const hintLevel = attemptNumber <= 1 ? 'gentle' : attemptNumber <= 2 ? 'moderate' : 'direct';

  const prompt = `A player is stuck on this math quest:

CHALLENGE: ${question}
${studentAnswer ? `THEIR ATTEMPT: ${studentAnswer}` : 'They haven\'t tried yet'}
SKILL: ${skillTag}
HINT LEVEL NEEDED: ${hintLevel} (attempt #${attemptNumber})

${hintLevel === 'gentle' 
  ? 'Give a small nudge without revealing the method.'
  : hintLevel === 'moderate'
  ? 'Explain the general approach without solving it.'
  : 'Walk them through the first step clearly.'}

Return JSON:
{
  "hint": "Your hint here (use gaming language, be encouraging)",
  "category": "conceptual|procedural|example",
  "difficulty": "${hintLevel}",
  "followUp": "Optional: what to try if still stuck"
}`;

  const response = await callVertexAI({
    prompt,
    systemInstruction: SYSTEM_INSTRUCTIONS.hints,
    maxTokens: 300,
    temperature: 0.6,
    userId: profile.userId,
    isChild: profile.isChild,
    operation: 'hint',
  });

  if (!response) {
    return getDefaultHint(skillTag, attemptNumber);
  }

  try {
    const jsonMatch = response.match(/\{[\s\S]*\}/);
    if (!jsonMatch) throw new Error('No JSON found');
    
    const hint = JSON.parse(jsonMatch[0]) as HintResponse;
    setCache(cacheKey, hint, CACHE_TTL.hints);
    return hint;
  } catch {
    return getDefaultHint(skillTag, attemptNumber);
  }
}

/**
 * Generate parent-friendly progress summary
 */
export async function getProgressSummary(profile: StudentProfile): Promise<ProgressSummary> {
  const cacheKey = getCacheKey('summary', { 
    userId: profile.userId,
    date: new Date().toISOString().split('T')[0],
  });

  const cached = getFromCache<ProgressSummary>(cacheKey);
  if (cached) {
    return cached;
  }

  const recentWins = profile.recentAttempts.filter(a => a.passed).length;
  const recentTotal = profile.recentAttempts.length;

  const prompt = `Create a brief progress summary for a parent about their child's learning:

CHILD'S STATS:
- Level: ${profile.currentLevel}
- Total XP earned: ${profile.totalXP}
- Current streak: ${profile.streakDays} days
- Recent challenges: ${recentWins}/${recentTotal} completed successfully
- Strongest skills: ${profile.masteredSkills.slice(0, 3).join(', ') || 'Building foundation'}
- Growth areas: ${profile.strugglingSkills.slice(0, 2).join(', ') || 'No major struggles identified'}

Recent activity:
${profile.recentAttempts.slice(0, 3).map(a => 
  `- ${a.skillTag}: ${a.passed ? '✓' : '○'} ${Math.round(a.score * 100)}%`
).join('\n')}

Return JSON:
{
  "headline": "One sentence celebration or encouragement",
  "strengths": ["2-3 specific things going well"],
  "growthAreas": ["1-2 areas for improvement, framed positively"],
  "recommendedActions": ["1-2 specific things parent can do to help"],
  "celebrationMoment": "Optional: something worth celebrating if applicable"
}`;

  const response = await callVertexAI({
    prompt,
    systemInstruction: SYSTEM_INSTRUCTIONS.parentSummary,
    maxTokens: 400,
    temperature: 0.5,
    userId: profile.userId,
    isChild: profile.isChild,
    operation: 'analysis',
  });

  if (!response) {
    return getDefaultSummary(profile);
  }

  try {
    const jsonMatch = response.match(/\{[\s\S]*\}/);
    if (!jsonMatch) throw new Error('No JSON found');
    
    const summary = JSON.parse(jsonMatch[0]) as ProgressSummary;
    setCache(cacheKey, summary, CACHE_TTL.summaries);
    return summary;
  } catch {
    return getDefaultSummary(profile);
  }
}

/**
 * Analyze error patterns to identify misconceptions
 */
export async function analyzeErrors(attempts: AttemptSummary[]): Promise<{
  patterns: string[];
  misconceptions: string[];
  recommendations: string[];
}> {
  if (attempts.length < 3) {
    return { patterns: [], misconceptions: [], recommendations: [] };
  }

  const failedAttempts = attempts.filter(a => !a.passed);
  if (failedAttempts.length === 0) {
    return { patterns: [], misconceptions: [], recommendations: [] };
  }

  const prompt = `Analyze these failed math challenges to identify learning patterns:

FAILED ATTEMPTS:
${failedAttempts.map(a => 
  `- ${a.skillTag}: Score ${Math.round(a.score * 100)}%, Errors: ${a.errorPatterns?.join(', ') || 'unknown'}`
).join('\n')}

Identify:
1. Common error patterns
2. Possible misconceptions
3. What would help this student

Return JSON:
{
  "patterns": ["Pattern descriptions"],
  "misconceptions": ["Possible underlying misunderstandings"],
  "recommendations": ["Specific practice recommendations"]
}`;

  const response = await callVertexAI({
    prompt,
    maxTokens: 400,
    temperature: 0.3,
    operation: 'analysis',
  });

  if (!response) {
    return { patterns: [], misconceptions: [], recommendations: [] };
  }

  try {
    const jsonMatch = response.match(/\{[\s\S]*\}/);
    if (!jsonMatch) throw new Error('No JSON found');
    return JSON.parse(jsonMatch[0]);
  } catch {
    return { patterns: [], misconceptions: [], recommendations: [] };
  }
}

// Fallback functions when AI is unavailable or rate-limited

function getDefaultRecommendations(
  profile: StudentProfile,
  challenges: Array<{ templateId: string; title: string; skillTag: string; difficulty: string }>
): ChallengeRecommendation[] {
  // Simple heuristic-based recommendations
  const struggling = new Set(profile.strugglingSkills);
  const mastered = new Set(profile.masteredSkills);

  return challenges
    .filter(c => !mastered.has(c.skillTag))
    .sort((a, b) => {
      // Prioritize struggling skills
      const aStruggling = struggling.has(a.skillTag) ? 1 : 0;
      const bStruggling = struggling.has(b.skillTag) ? 1 : 0;
      return bStruggling - aStruggling;
    })
    .slice(0, 3)
    .map(c => ({
      templateId: c.templateId,
      title: c.title,
      skillTag: c.skillTag,
      reason: struggling.has(c.skillTag) 
        ? "Time to level up this skill! You've got this!"
        : "New quest unlocked! Ready for a challenge?",
      difficulty: c.difficulty as 'easy' | 'medium' | 'hard',
      estimatedTime: '5-10 min',
      xpReward: c.difficulty === 'hard' ? 50 : c.difficulty === 'medium' ? 30 : 20,
      confidence: 0.6,
    }));
}

function getDefaultHint(skillTag: string, attemptNumber: number): HintResponse {
  const hints: Record<string, HintResponse[]> = {
    'calc.power_rule': [
      {
        hint: "Remember: when you see x^n, the power comes down and multiplies! 💪",
        category: 'conceptual',
        difficulty: 'gentle',
        followUp: "Think about what happens to the exponent after it comes down.",
      },
      {
        hint: "The power rule says: multiply by the exponent, then subtract 1 from it. Try that!",
        category: 'procedural',
        difficulty: 'moderate',
      },
      {
        hint: "For ax^n, the derivative is (a×n)x^(n-1). So for 5x³, you'd get 15x².",
        category: 'example',
        difficulty: 'direct',
      },
    ],
  };

  const skillHints = hints[skillTag] || hints['calc.power_rule'];
  const hintIndex = Math.min(attemptNumber - 1, skillHints.length - 1);
  return skillHints[hintIndex];
}

function getDefaultSummary(profile: StudentProfile): ProgressSummary {
  const recentWins = profile.recentAttempts.filter(a => a.passed).length;
  const streak = profile.streakDays;

  return {
    headline: streak >= 3 
      ? `🔥 ${streak}-day streak! Your child is on a roll!`
      : recentWins > 0 
      ? "Making progress every day!"
      : "Just getting started - every expert was once a beginner!",
    strengths: profile.masteredSkills.length > 0
      ? [`Strong grasp of ${profile.masteredSkills[0]}`]
      : ["Building a solid foundation"],
    growthAreas: profile.strugglingSkills.length > 0
      ? [`${profile.strugglingSkills[0]} needs a bit more practice`]
      : ["Keep up the consistent practice!"],
    recommendedActions: [
      "Celebrate small wins to keep motivation high",
      "Try to maintain the daily practice habit",
    ],
    celebrationMoment: profile.totalXP >= 100 
      ? `Earned over ${profile.totalXP} XP total!` 
      : undefined,
  };
}
